import type { ReactNode } from "react";
import { Spinner } from "../atoms/Spinner";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { ErrorState } from "../molecules/ErrorState";
import { CertificateViewer } from "../organisms/CertificateViewer";
import {
	ReportPreview,
	type ReportPreviewProps,
} from "../organisms/ReportPreview";

export interface ReportPreviewPageProps {
	report: {
		kind: ReportPreviewProps["kind"];
		target: string;
		metadata: Record<string, string>;
		proofData?: ReportPreviewProps["proofData"];
		pdfUrl?: string | null;
	} | null;
	generating?: boolean;
	error?: Error | null;
	onDownload: () => void;
	onPrint: () => void;
	className?: string;
}

function ReportPreviewPage({
	report,
	generating = false,
	error = null,
	onDownload,
	onPrint,
	className,
}: ReportPreviewPageProps): ReactNode {
	if (error) {
		return (
			<ErrorState
				error={error}
				title="Failed to generate report"
				className={cn("py-24", className)}
			/>
		);
	}

	if (generating) {
		return (
			<div
				className={cn(
					"flex flex-col items-center justify-center gap-4 py-24",
					className,
				)}
			>
				<Spinner size="lg" label="Generating report..." />
				<Text textColor="mutedForeground">
					Generating report, please wait...
				</Text>
			</div>
		);
	}

	if (!report) {
		return (
			<Text
				textColor="mutedForeground"
				className={cn("py-24 text-center", className)}
			>
				No report selected.
			</Text>
		);
	}

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			<ReportPreview
				kind={report.kind}
				target={report.target}
				metadata={report.metadata}
				proofData={report.proofData}
				onDownload={onDownload}
				onPrint={onPrint}
			/>

			{report.pdfUrl && (
				<CertificateViewer
					pdfUrl={report.pdfUrl}
					title={`${report.target} Certificate`}
				/>
			)}
		</div>
	);
}
ReportPreviewPage.displayName = "ReportPreviewPage";

export default ReportPreviewPage;
export { ReportPreviewPage };
